import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { settingsApi, ssoApi } from '../services/api';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Shield, ArrowLeft, Mail, Loader2 } from 'lucide-react';

const DEFAULT_LOGO = 'https://customer-assets.emergentagent.com/job_fpa-builder/artifacts/rrkna3wt_CGES%20Logo%20Transparent%20Background.png';

// Get cached branding from localStorage for instant display
const getCachedBranding = () => {
  try {
    const cached = localStorage.getItem('branding');
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
};

const LoginPage = () => {
  const [searchParams] = useSearchParams();
  const [view, setView] = useState('login');
  const [loading, setLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // Initialize with cached branding for instant display
  const [branding, setBranding] = useState(getCachedBranding);
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const { login, loginWithToken } = useAuth();
  const navigate = useNavigate();
  
  const resetToken = searchParams.get('token');

  useEffect(() => {
    // Load branding and SSO status
    Promise.all([
      settingsApi.getBranding().catch(() => ({ data: null })),
      ssoApi.getStatus().catch(() => ({ data: { enabled: false } }))
    ]).then(([brandingRes, ssoRes]) => {
      if (brandingRes.data) {
        setBranding(brandingRes.data);
        localStorage.setItem('branding', JSON.stringify(brandingRes.data));
      }
      setSsoEnabled(ssoRes.data?.enabled || false);
    });
    
    if (resetToken) {
      setView('reset');
    }
  }, [resetToken]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const userData = await login(email, password);
      
      // Check if password change required
      if (userData.must_change_password) {
        toast.info('Please change your password');
        navigate('/change-password');
      } else {
        toast.success('Welcome back!');
        navigate('/');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await api.post('/auth/forgot-password', { email });
      toast.success('If the email exists, a reset link has been sent');
      setView('login');
    } catch (error) {
      toast.error('Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      await api.post('/auth/reset-password', { 
        token: resetToken, 
        new_password: newPassword 
      });
      toast.success('Password reset successfully! Please login.');
      setView('login');
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const handleSSOLogin = async () => {
    setSsoLoading(true);
    
    try {
      const response = await ssoApi.getLoginUrl();
      const { auth_url, state } = response.data;
      
      // Store state for verification after redirect
      sessionStorage.setItem('sso_state', state);
      
      // Redirect to Microsoft login
      window.location.href = auth_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate SSO login');
      setSsoLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel - Hero */}
      <div className="hidden lg:flex lg:w-1/2 bg-zinc-900 relative overflow-hidden flex-col">
        {/* Background Image - show immediately if URL exists */}
        {branding?.signin_background_url && (
          <img 
            src={branding.signin_background_url}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        
        {/* Fallback gradient when no image */}
        {!branding?.signin_background_url && (
          <div className="absolute inset-0 bg-gradient-to-br from-zinc-800 via-zinc-900 to-emerald-900/30" />
        )}
        
        {/* Dark Overlay for Readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-900/85 via-zinc-900/70 to-zinc-900/90" />
        
        {/* Centered Hero Content - Full height centered */}
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center p-12">
          {/* Large Centered Logo */}
          <div className="flex flex-col items-center text-center">
            {/* Logo Container with white background for logos with grey */}
            <div className="bg-white rounded-2xl p-6 mb-8 shadow-lg">
              <img 
                src={branding?.logo_url || DEFAULT_LOGO} 
                alt={branding?.company_name || 'C-Ges'}
                className="w-40 h-40 object-contain"
                data-testid="login-logo"
              />
            </div>
            
            {/* Company Name - Bold and Prominent */}
            <h1 className="font-display text-4xl font-bold text-white tracking-wide drop-shadow-lg mb-4">
              {branding?.company_name || 'C-Ges FP&A'}
            </h1>
            
            {/* Tagline */}
            <p className="text-white/80 text-lg font-light max-w-sm leading-relaxed">
              Financial Planning & Analysis Platform
            </p>
          </div>
          
          {/* Subtle Footer */}
          <div className="absolute bottom-8 left-0 right-0 text-center">
            <p className="text-white/40 text-sm">
              Powered by FP&A Platform
            </p>
          </div>
        </div>
      </div>

      {/* Right Panel - Auth Forms */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-slate-50">
        <Card className="w-full max-w-md border border-slate-200 shadow-none rounded-sm">
          <CardHeader className="space-y-1 pb-4">
            {/* Mobile Logo - Larger and Centered */}
            <div className="lg:hidden flex flex-col items-center mb-6">
              <img 
                src={branding?.logo_url || DEFAULT_LOGO} 
                alt={branding?.company_name || 'C-Ges'}
                className="w-24 h-24 object-contain mb-3"
              />
              <span className="font-display text-xl font-bold text-slate-900">
                {branding?.company_name || 'C-Ges FP&A'}
              </span>
            </div>
            
            {view === 'login' && (
              <>
                <CardTitle className="font-display text-2xl font-medium">Sign in</CardTitle>
                <CardDescription className="text-slate-500">
                  Enter your credentials to access your dashboard
                </CardDescription>
              </>
            )}
            
            {view === 'forgot' && (
              <>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => setView('login')}
                    className="text-slate-500 hover:text-slate-900"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <CardTitle className="font-display text-2xl font-medium">Forgot Password</CardTitle>
                </div>
                <CardDescription className="text-slate-500">
                  Enter your email to receive a password reset link
                </CardDescription>
              </>
            )}
            
            {view === 'reset' && (
              <>
                <CardTitle className="font-display text-2xl font-medium">Reset Password</CardTitle>
                <CardDescription className="text-slate-500">
                  Enter your new password
                </CardDescription>
              </>
            )}
          </CardHeader>
          
          <CardContent>
            {/* Login Form */}
            {view === 'login' && (
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium">Email</Label>
                  <Input
                    id="email"
                    data-testid="email-input"
                    type="email"
                    placeholder="name@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="rounded-sm border-slate-200 focus:ring-zinc-900"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                    <button 
                      type="button"
                      onClick={() => setView('forgot')}
                      className="text-sm text-zinc-600 hover:text-zinc-900 hover:underline"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <Input
                    id="password"
                    data-testid="password-input"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="rounded-sm border-slate-200 focus:ring-zinc-900"
                  />
                </div>
                
                <Button 
                  type="submit" 
                  data-testid="submit-btn"
                  className="w-full bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm h-11 font-medium"
                  disabled={loading}
                >
                  {loading ? 'Signing in...' : 'Sign in'}
                </Button>

                {ssoEnabled && (
                  <>
                    <div className="relative my-6">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-slate-200"></div>
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-white px-2 text-slate-400">Or continue with</span>
                      </div>
                    </div>

                    <Button 
                      variant="outline" 
                      className="w-full rounded-sm border-slate-200 hover:bg-slate-50 h-11"
                      data-testid="microsoft-sso-btn"
                      onClick={handleSSOLogin}
                      disabled={ssoLoading}
                    >
                      {ssoLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Redirecting...
                        </>
                      ) : (
                        <>
                          <Shield className="w-4 h-4 mr-2" strokeWidth={1.5} />
                          Sign in with Microsoft
                        </>
                      )}
                    </Button>
                  </>
                )}

                <p className="text-center text-sm text-slate-500 mt-6">
                  Don't have an account? Contact your administrator.
                </p>
              </form>
            )}

            {/* Forgot Password Form */}
            {view === 'forgot' && (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="forgot-email" className="text-sm font-medium">Email</Label>
                  <Input
                    id="forgot-email"
                    data-testid="forgot-email-input"
                    type="email"
                    placeholder="name@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="rounded-sm border-slate-200 focus:ring-zinc-900"
                  />
                </div>
                
                <Button 
                  type="submit" 
                  data-testid="send-reset-btn"
                  className="w-full bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm h-11 font-medium"
                  disabled={loading}
                >
                  {loading ? (
                    'Sending...'
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Send Reset Link
                    </>
                  )}
                </Button>

                <p className="text-center text-sm text-slate-500 mt-4">
                  Remember your password?{' '}
                  <button 
                    type="button"
                    onClick={() => setView('login')}
                    className="text-zinc-900 font-medium hover:underline"
                  >
                    Sign in
                  </button>
                </p>
              </form>
            )}

            {/* Reset Password Form */}
            {view === 'reset' && (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password" className="text-sm font-medium">New Password</Label>
                  <Input
                    id="new-password"
                    data-testid="new-password-input"
                    type="password"
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                    className="rounded-sm border-slate-200 focus:ring-zinc-900"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password" className="text-sm font-medium">Confirm Password</Label>
                  <Input
                    id="confirm-password"
                    data-testid="confirm-password-input"
                    type="password"
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={8}
                    className="rounded-sm border-slate-200 focus:ring-zinc-900"
                  />
                </div>
                
                <Button 
                  type="submit" 
                  data-testid="reset-password-btn"
                  className="w-full bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm h-11 font-medium"
                  disabled={loading}
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
